import React, { useCallback, useMemo, useRef, useState } from 'react';
import SubtitleLayerEditor from '@/components/subtitles/SubtitleLayerEditor';
import { computeObjectContainContentRect } from '@/lib/videoSubtitlePreview';
import {
  getSubtitleSafeInsets,
  normalizeSubtitleLayer,
  subtitleLayerFromLegacyAnchors,
} from '@/lib/subtitles/subtitleLayer';

/**
 * Subtitle overlay scoped to the object-contain preview frame.
 * Uses normalized coordinates; scales with preview container and export aspect ratio.
 */
export default function SubtitlePreviewOverlay({
  stageRef,
  stageWidth,
  stageHeight,
  contentRect,
  aspectRatio,
  viewportMin,
  subtitleStyle,
  layer: layerProp,
  text,
  editText = null,
  tokens = null,
  highlightColor = '#fde047',
  isActive,
  interactive = true,
  snapToGrid = false,
  showSafeAreaGuide = false,
  onLayerChange,
  onTextChange,
}) {
  const [interacting, setInteracting] = useState(false);
  const frameScreenRectRef = useRef(null);

  const resolvedLayer = useMemo(() => {
    if (layerProp) return normalizeSubtitleLayer(layerProp, aspectRatio);
    if (subtitleStyle?.layer) return normalizeSubtitleLayer(subtitleStyle.layer, aspectRatio);
    return subtitleLayerFromLegacyAnchors(
      subtitleStyle?.anchorX,
      subtitleStyle?.anchorY,
      aspectRatio,
    );
  }, [layerProp, subtitleStyle, aspectRatio]);

  const measuredStage = useMemo(() => {
    if (stageWidth > 0 && stageHeight > 0) {
      const rect = contentRect?.width > 0
        ? contentRect
        : computeObjectContainContentRect(stageWidth, stageHeight, aspectRatio);
      return { stageWidth, stageHeight, contentRect: rect };
    }
    const stage = stageRef?.current;
    if (!stage) {
      return { stageWidth, stageHeight, contentRect };
    }
    const bounds = stage.getBoundingClientRect();
    const w = Math.round(bounds.width);
    const h = Math.round(bounds.height);
    if (w < 1 || h < 1) {
      return { stageWidth, stageHeight, contentRect };
    }
    return {
      stageWidth: w,
      stageHeight: h,
      contentRect: computeObjectContainContentRect(w, h, aspectRatio),
    };
  }, [stageWidth, stageHeight, contentRect, stageRef, aspectRatio]);

  const frameW = measuredStage.contentRect?.width || measuredStage.stageWidth;
  const frameH = measuredStage.contentRect?.height || measuredStage.stageHeight;
  const frameLeft = measuredStage.contentRect?.left ?? 0;
  const frameTop = measuredStage.contentRect?.top ?? 0;

  const updateFrameScreenRect = useCallback(() => {
    const stage = stageRef?.current;
    if (!stage) return null;
    const sr = stage.getBoundingClientRect();
    const cr = measuredStage.contentRect?.width
      ? measuredStage.contentRect
      : computeObjectContainContentRect(sr.width, sr.height, aspectRatio);
    const rect = {
      left: sr.left + (cr.left || 0),
      top: sr.top + (cr.top || 0),
      width: cr.width || sr.width,
      height: cr.height || sr.height,
    };
    frameScreenRectRef.current = rect;
    return rect;
  }, [stageRef, measuredStage.contentRect, aspectRatio]);

  const handleLayerChange = useCallback(
    (nextLayer) => {
      onLayerChange?.(normalizeSubtitleLayer(nextLayer, aspectRatio));
    },
    [aspectRatio, onLayerChange],
  );

  const frameScreenRect = updateFrameScreenRect() || {
    left: 0,
    top: 0,
    width: frameW,
    height: frameH,
  };

  return (
    <div className="absolute inset-0 pointer-events-none z-[30]">
      <div
        className="absolute overflow-visible"
        style={{
          left: frameLeft,
          top: frameTop,
          width: frameW,
          height: frameH,
        }}
      >
        {showSafeAreaGuide ? (
          <SafeAreaGuide aspectRatio={aspectRatio} interacting={interacting} />
        ) : null}
        <div className="absolute inset-0 pointer-events-none">
          <SubtitleLayerEditor
            layer={resolvedLayer}
            style={subtitleStyle}
            text={text}
            editText={editText}
            tokens={tokens}
            highlightColor={highlightColor}
            frameWidth={frameW}
            frameHeight={frameH}
            frameScreenRect={frameScreenRect}
            aspectRatio={aspectRatio}
            viewportMin={viewportMin}
            isActive={isActive}
            interactive={interactive}
            snapToGrid={snapToGrid}
            onLayerChange={handleLayerChange}
            onTextChange={onTextChange}
            onInteractionStart={() => {
              updateFrameScreenRect();
              setInteracting(true);
            }}
            onInteractionEnd={() => setInteracting(false)}
          />
        </div>
      </div>
    </div>
  );
}

function SafeAreaGuide({ aspectRatio, interacting }) {
  const safe = getSubtitleSafeInsets(aspectRatio);
  return (
    <div
      className="absolute pointer-events-none rounded-sm border border-dashed border-amber-400/40"
      style={{
        left: `${safe.left * 100}%`,
        top: `${safe.top * 100}%`,
        width: `${(1 - safe.left - safe.right) * 100}%`,
        height: `${(1 - safe.top - safe.bottom) * 100}%`,
        opacity: interacting ? 0.55 : 0.2,
      }}
      aria-hidden
    />
  );
}
